import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CategoryService } from '../../api/category.service';
import { StoreListService } from '../../api/store.service';
import { SitePagesService } from '../../api/site-pages.service';
import { StoreService } from '../../services/store.service';
import { BRAND } from 'src/app/shared/brand';
import { CultureService } from '../../culture/culture.service';

/// What ApplyCode and ClaimCode answer about the phone code, and NewStoreRequest and VerifyStoreRequest about a code typed
/// (StoreController.PhoneCodeAnswer). A refusal of the application itself is a number, as before.
interface PhoneCodeAnswer {
	result: string;
	ticket?: string;
	wait?: number;
	triesLeft?: number;
	phone?: string;
}

/// The digits of a Bangladeshi mobile number however it was typed - Bangla digits, +880, spaces - or null when it is not one
/// (PhoneNumbers.BdMobile on the server, which decides).
export function bdMobile(raw: string): string | null {
	const latin = String(raw || '').replace(/[\u09E6-\u09EF]/g, d => String(d.charCodeAt(0) - 0x09E6));
	let digits = latin.replace(/\D/g, '');
	if (digits.startsWith('880')) { digits = digits.substring(2); }
	return /^01[3-9]\d{8}$/.test(digits) ? digits : null;
}

function bdMobileValidator(control: AbstractControl): ValidationErrors | null {
	return !control.value || bdMobile(control.value) ? null : { bdMobile: true };
}

/**
 * Two jobs behind one modal: applying to sell here, and claiming a shop
 * that is already listed. `store` decides which - it is set only for a claim.
 *
 * Both prove the phone first (2026-10-22): once the form is complete, the server checks it as it will when it is sent and
 * texts a 6-digit code to the phone; the application or claim then goes with the code. While the site has no SMS gateway
 * the server says so ("off") and it goes without one.
 *
 * This form was written for another site and still said so: the copy named
 * cheapdam.com, the category list offered "Smartphone", "Refrigaretor, Freezer, TV"
 * and "Bike" - none of which are categories here - and the location dropdown read
 * from a table with no rows, so it rendered empty and could never be satisfied.
 * Worst of all it refused to submit without at least one brand, matched against a
 * brands table that is empty, so no application could be completed at all.
 */
@Component({
	selector: 'app-shop-and-featuring-request-form',
	templateUrl: './enlisting-request-form.component.html',
	styleUrls: ['./enlisting-request-form.component.scss']
})
export class ShopAndFeaturingRequestFormComponent implements OnInit, OnDestroy {
	/// The brand, for the template (shared/brand.ts).
	readonly brand = BRAND;


	@Input() modalRef: BsModalRef;
	@Input() locations: any[];
	@Input() store: any;
	/// Drawn on a page of its own (/sell/apply) rather than in the modal: no close button or Cancel, and the form
	/// grows with the page instead of scrolling inside a fixed height.
	@Input() page = false;
	/// The application or claim is in, and the form shows what happens next.
	@Output() sent = new EventEmitter<void>();

	private destroy$: Subject<void> = new Subject();

	/// Real categories, loaded from the live tree rather than hardcoded.
	categories: { slug: string; name: string }[] = [];
	categoriesFailed = false;

	calling = false;
	submitted = false;

	/// Set once the application or claim is in: the form gives way to a panel saying what happens next.
	done: { name: string; shop: string; email: string; claim: boolean } = null;

	/// The countries the shop knows (typeInfos "System.Country": Value = id, ValueText = ISO2, Name), for the country
	/// select; the applicant's own is preselected from Locale/me once it has answered.
	countries: any[] = [];

	/// "form" while the details are filled in; "code" once a code has been texted to the phone and is waited for.
	step: 'form' | 'code' = 'form';
	/// The code as typed, and what is wrong with it.
	readonly code = new FormControl('');
	codeError: string = null;
	/// The ticket the code was texted on, and the number it went to as it reads back ("01712-345678").
	private codeTicket: string = null;
	codePhone: string = null;
	/// Seconds until "send the code again" may be pressed.
	resendIn = 0;
	private resendTimer: any = null;

	/// The contact form's signed token, which asking for a code must carry; fetched when the form is shown, again when stale.
	private token: string = null;

	private readonly browser: boolean;

	constructor(
		private formBuilder: FormBuilder,
		private catservice: CategoryService,
		private toastr: ToastrService,
		private storeSer: StoreListService,
		private culture: StoreService,
		private host: ElementRef<HTMLElement>,
		private labels: CultureService,
		private pages: SitePagesService,
		@Inject(PLATFORM_ID) platformId: any) {
		this.browser = isPlatformBrowser(platformId);
	}

	/// One shape for both modes. The claim flow ignores the fields it does not need
	/// rather than maintaining a second FormGroup that duplicated every validator.
	form: FormGroup = this.formBuilder.group({
		name: new FormControl('', [Validators.required, Validators.maxLength(120)]),
		email: new FormControl('', [Validators.required, Validators.email]),
		// A Bangladeshi mobile: the code that proves it goes by SMS.
		phone: new FormControl('', [Validators.required, Validators.maxLength(40), bdMobileValidator]),
		storeName: new FormControl('', [Validators.required, Validators.maxLength(120)]),
		category: new FormControl('', Validators.required),
		storeLocation: new FormControl('', [Validators.required, Validators.maxLength(80)]),
		storeAddress: new FormControl('', [Validators.required, Validators.maxLength(400)]),
		website: new FormControl('', Validators.maxLength(200)),
		about: new FormControl('', Validators.maxLength(400)),
		// The applicant's country (ISO2): the seller account made at approval takes its locale from it.
		country: new FormControl('', Validators.required),
		// Agreement to the terms is part of applying; the server refuses without it and keeps the moment.
		agreed: new FormControl(false, Validators.requiredTrue)
	});

	ngOnInit(): void {
		this.toastr.toastrConfig.preventDuplicates = true;
		this.fetchToken();

		if (this.store) {
			// Claiming a listed shop: its details are shown read-only, so they are not
			// validated - only the contact fields are the applicant's to fill in.
			this.form.patchValue({
				storeName: this.store.name,
				storeAddress: this.store.address,
				storeLocation: this.store.location || this.store.locationSlug
			});

			['category', 'storeName', 'storeAddress', 'storeLocation', 'country'].forEach(c => {
				this.form.get(c).clearValidators();
				this.form.get(c).updateValueAndValidity();
			});
			return;
		}

		this.loadCategories();
		this.loadCountries();
	}

	/// The list as the signup page gets it, and the applicant's own country as the start - not the guess made
	/// before Locale/me answers (Pending), and never over a country the applicant picked.
	private loadCountries(): void {
		this.culture.onCultureDataChange$.pipe(takeUntil(this.destroy$)).subscribe(res => {
			if (res) {
				this.countries = (this.culture.cultureState.typeInfos && this.culture.cultureState.typeInfos['System.Country']) || [];
				this.preselectCountry();
			}
		});
		this.culture.onCountry.pipe(takeUntil(this.destroy$)).subscribe(visitor => {
			if (visitor && !visitor.Pending && visitor.CountryCode) {
				this.visitorCountry = String(visitor.CountryCode).toUpperCase();
				this.preselectCountry();
			}
		});
	}

	/// Where Locale/me says the applicant is; the select starts there.
	private visitorCountry: string = null;

	private preselectCountry(): void {
		const control = this.form.get('country');
		if (!control || control.dirty || control.value || !this.visitorCountry || !this.countries.length) { return; }
		const own = this.countries.find(c => c.ValueText && String(c.ValueText).toUpperCase() === this.visitorCountry);
		if (own) { control.setValue(own.ValueText); }
	}

	ngOnDestroy(): void {
		this.stopResendTimer();
		this.destroy$.next();
		this.destroy$.complete();
	}

	private loadCategories(): void {
		this.catservice.getAllParentCategory().pipe(takeUntil(this.destroy$)).subscribe(
			res => {
				this.categories = (res || []).map(c => ({ slug: c.slug, name: c.name }));
				this.categoriesFailed = this.categories.length === 0;
			},
			() => {
				// Without categories the applicant cannot pick one, so let them send the
				// application anyway rather than trapping them behind a failed request.
				this.categoriesFailed = true;
				this.form.get('category').clearValidators();
				this.form.get('category').updateValueAndValidity();
			});
	}

	get isClaim(): boolean {
		return !!this.store;
	}

	/// True once the field should show its error - touched, or a submit was attempted.
	invalid(field: string): boolean {
		const control = this.form.get(field);
		return control.invalid && (control.touched || this.submitted);
	}

	submit(): void {
		if (this.calling) { return; }
		if (this.step === 'code') {
			this.sendWithCode();
			return;
		}

		this.submitted = true;

		// Trim before validating, so a field of spaces is not accepted as filled.
		Object.keys(this.form.controls).forEach(key => {
			const control = this.form.get(key);
			if (typeof control.value === 'string') {
				control.setValue(control.value.trim(), { emitEvent: false });
			}
			control.markAsTouched();
		});

		if (this.form.invalid) {
			this.toastr.error(this.labels.t('seller-apply-complete-fields', 'Please complete the highlighted fields.'));
			this.showFirstInvalid();
			return;
		}

		this.calling = true;
		this.askForCode(false, false);
	}

	/// What the server is sent: the application, or the claim.
	private details(): { [field: string]: any } {
		const value = this.form.value;
		if (this.isClaim) {
			return {
				Name: value.name,
				Email: value.email,
				Phone: value.phone,
				StoreId: this.store.id,
				AgreedToTerms: value.agreed === true
			};
		}
		return {
			Name: value.name,
			Email: value.email,
			Phone: value.phone,
			StoreName: value.storeName,
			StoreAddress: value.storeAddress,
			StoreLocation: value.storeLocation,
			Category: value.category,
			About: value.about,
			Website: value.website,
			CountryCode: value.country,
			AgreedToTerms: value.agreed === true,
			// Brands are chosen later, from the seller dashboard - requiring one here
			// blocked every application while the brand table was empty.
			Brands: ''
		};
	}

	// ---- the phone code ------------------------------------------------------------------------------------------------

	private fetchToken(then?: () => void): void {
		if (!this.browser) { return; }
		this.pages.formToken().pipe(takeUntil(this.destroy$)).subscribe(token => {
			this.token = token;
			if (then) { then(); }
		});
	}

	/// Step one: the server checks the application or claim as it will when it is sent, then texts the code to its phone.
	/// Again: the same ticket gets a new code.
	private askForCode(again: boolean, retried: boolean): void {
		const body = { ...this.details(), Token: this.token, PhoneTicket: again ? this.codeTicket : null };
		const ask: Observable<any> = this.isClaim ? this.storeSer.claimCode(body) : this.storeSer.applyCode(body);
		ask.pipe(takeUntil(this.destroy$)).subscribe(
			res => this.afterCodeAsked(res, again, retried),
			() => this.failed());
	}

	private afterCodeAsked(res: any, again: boolean, retried: boolean): void {
		if (!res || typeof res !== 'object') {
			// The application or claim itself was refused: the same answers as sending it.
			if (this.isClaim) { this.afterClaim(res); } else { this.afterApply(res); }
			return;
		}
		const answer = res as PhoneCodeAnswer;
		if (answer.result === 'retry' && !retried) {
			// The form's token was stale (a deploy, a form left open): a fresh one, and the same request once more.
			this.fetchToken(() => this.askForCode(again, true));
			return;
		}
		this.calling = false;
		switch (answer.result) {
			case 'sent':
				this.codeTicket = answer.ticket;
				this.codePhone = answer.phone;
				this.step = 'code';
				this.code.setValue('');
				this.codeError = null;
				this.startResendTimer(answer.wait || 60);
				if (again) { this.toastr.success(this.labels.t('seller-apply-code-sent-again', 'A new code is on its way.')); }
				this.focusCode();
				break;
			case 'off':
				// The site cannot text: the application or claim goes without a code.
				this.calling = true;
				this.sendFinal(null);
				break;
			case 'wait':
				if (this.step === 'code') {
					this.startResendTimer(answer.wait || 60);
				} else {
					this.toastr.info(this.labels.t('seller-apply-wait-seconds', 'Please wait {n} seconds and try again.', { n: answer.wait || 3 }));
				}
				break;
			case 'phone':
				this.phoneRefused();
				break;
			case 'too-many':
				this.toastr.error(this.labels.t('seller-apply-code-too-many', 'Too many codes for this number. Please try again tomorrow.'), this.labels.t('not-sent', 'Not sent'), { timeOut: 10000 });
				break;
			default:
				// "busy", "failed", a second stale token.
				this.toastr.error(this.labels.t('seller-apply-code-not-sent', 'We could not text a code just now. Please try again in a few minutes.'), this.labels.t('not-sent', 'Not sent'), { timeOut: 10000 });
		}
	}

	/// Step two: the application or claim with the code typed.
	private sendWithCode(): void {
		const digits = String(this.code.value || '').replace(/[\u09E6-\u09EF]/g, d => String(d.charCodeAt(0) - 0x09E6)).replace(/\D/g, '');
		if (digits.length !== 6) {
			this.codeError = this.labels.t('seller-apply-code-six-digits', 'Enter the 6-digit code from the SMS.');
			this.focusCode();
			return;
		}
		this.calling = true;
		this.sendFinal(digits);
	}

	private sendFinal(code: string | null): void {
		const body = { ...this.details(), PhoneTicket: code ? this.codeTicket : null, PhoneCode: code };
		if (this.isClaim) {
			this.storeSer.verifyStoreRequest(body).pipe(takeUntil(this.destroy$)).subscribe(
				res => { if (!this.codeRefused(res)) { this.afterClaim(res); } },
				() => this.failed());
			return;
		}
		this.storeSer.newStoreRequest(body).pipe(takeUntil(this.destroy$)).subscribe(
			res => { if (!this.codeRefused(res)) { this.afterApply(res); } },
			() => this.failed());
	}

	/// The server's answer about the code, said under the code box; false when the answer is not about the code.
	private codeRefused(res: any): boolean {
		if (!res || typeof res !== 'object' || !res.result) { return false; }
		const answer = res as PhoneCodeAnswer;
		this.calling = false;
		switch (answer.result) {
			case 'code-wrong':
				this.codeError = this.labels.t('seller-apply-code-wrong', 'That code is not right. {n} tries left.', { n: answer.triesLeft });
				break;
			case 'code-expired':
				this.codeError = this.labels.t('seller-apply-code-expired', 'This code has expired. Send a new one.');
				break;
			case 'code-locked':
				this.codeError = this.labels.t('seller-apply-code-locked', 'Too many wrong tries. Send a new code.');
				break;
			case 'code-unknown':
				this.codeError = this.labels.t('seller-apply-code-unknown', 'This code no longer works. Send a new one.');
				break;
			case 'too-many':
				this.toastr.error(this.labels.t('seller-apply-code-tries-hour', 'Too many tries from here. Please try again in an hour.'), this.labels.t('not-sent', 'Not sent'), { timeOut: 10000 });
				break;
			default:
				this.failed();
		}
		if (this.codeError) { this.focusCode(); }
		return true;
	}

	/// "Send the code again", once the minute is up.
	resend(): void {
		if (this.calling || this.resendIn > 0) { return; }
		this.calling = true;
		this.codeError = null;
		this.askForCode(true, false);
	}

	/// Back to the form, to put another number in; a new code goes to it.
	changeNumber(): void {
		this.step = 'form';
		this.codeTicket = null;
		this.codeError = null;
		this.stopResendTimer();
		setTimeout(() => {
			const phone = this.host.nativeElement.querySelector('#sa-phone') as HTMLInputElement;
			if (phone) { phone.focus(); phone.select(); }
		});
	}

	private startResendTimer(seconds: number): void {
		this.stopResendTimer();
		this.resendIn = Math.max(0, Math.round(seconds));
		if (!this.browser || this.resendIn === 0) { return; }
		this.resendTimer = setInterval(() => {
			this.resendIn = Math.max(0, this.resendIn - 1);
			if (this.resendIn === 0) { this.stopResendTimer(); }
		}, 1000);
	}

	private stopResendTimer(): void {
		if (this.resendTimer) { clearInterval(this.resendTimer); }
		this.resendTimer = null;
	}

	private focusCode(): void {
		setTimeout(() => {
			const input = this.host.nativeElement.querySelector('#sa-code') as HTMLInputElement;
			if (input) { input.focus(); }
		});
	}

	/// The server says the phone is no Bangladeshi mobile: back to the form, with the phone field marked.
	private phoneRefused(): void {
		this.calling = false;
		this.step = 'form';
		this.stopResendTimer();
		this.form.get('phone').setErrors({ bdMobile: true });
		this.form.get('phone').markAsTouched();
		this.showFirstInvalid();
	}

	// ---- the answers ---------------------------------------------------------------------------------------------------

	private afterApply(res: any): void {
		this.calling = false;

		if (res === 1) {
			this.toastr.warning(
				this.labels.t('seller-apply-already-listed', 'A shop with this name, email or phone is already listed. Use "claim this shop" on its page if it is yours.'),
				this.labels.t('seller-apply-already-listed-title', 'Already listed'), { timeOut: 10000 });
		} else if (res === 2) {
			this.toastr.error(this.labels.t('seller-apply-agree-to-apply', 'Please agree to the terms to apply.'), this.labels.t('not-sent', 'Not sent'));
		} else if (res === 4) {
			this.toastr.error(this.labels.t('seller-apply-country-required', 'Please choose your country.'), this.labels.t('not-sent', 'Not sent'));
		} else if (res === 5) {
			this.toastr.error(this.labels.t('seller-apply-name-taken-by-site', 'Choose a name of your own: a shop cannot be named after this site.'), this.labels.t('not-sent', 'Not sent'), { timeOut: 10000 });
		} else if (res === 6) {
			this.toastr.error(this.labels.t('seller-apply-too-many', 'Too many applications from here in the last hour. Please try again later.'), this.labels.t('not-sent', 'Not sent'), { timeOut: 10000 });
		} else if (res === 7) {
			this.phoneRefused();
		} else if (res === true) {
			this.showDone(false);
		} else {
			this.failed();
		}
	}

	private afterClaim(res: any): void {
		this.calling = false;

		if (res === 3) {
			this.toastr.warning(this.labels.t('seller-claim-already-pending', 'There is already a pending claim for this shop. We will be in touch.'),
				this.labels.t('seller-claim-pending-title', 'Claim pending'), { timeOut: 10000 });
		} else if (res === 2) {
			this.toastr.error(this.labels.t('seller-claim-agree-to-send', 'Please agree to the terms to send the claim.'), this.labels.t('not-sent', 'Not sent'));
		} else if (res === 7) {
			this.phoneRefused();
		} else if (res === 1) {
			this.showDone(true);
		} else {
			this.failed();
		}
	}

	private showDone(claim: boolean): void {
		const value = this.form.value;
		this.stopResendTimer();
		// The name as typed: a first word is often a title or an initial ("Md. Rahim Uddin").
		this.done = {
			name: String(value.name || '').trim(),
			shop: value.storeName,
			email: value.email,
			claim
		};
		this.sent.emit();
	}

	/// The first field the submit marked, scrolled into the middle of the form and focused - the agreement sits at
	/// the bottom, out of sight of a Send pressed with the form scrolled to the top.
	private showFirstInvalid(): void {
		setTimeout(() => {
			const first = this.host.nativeElement.querySelector('.is-invalid') as HTMLElement;
			if (!first) { return; }
			const field = (first.matches('input, select, textarea') ? first : first.querySelector('input, select, textarea')) as HTMLElement;
			first.scrollIntoView({ behavior: 'smooth', block: 'center' });
			if (field) { field.focus({ preventScroll: true }); }
		});
	}

	private failed(): void {
		this.calling = false;
		this.toastr.error(this.labels.t('something-went-wrong-moment', 'Something went wrong. Please try again in a moment.'), this.labels.t('error-title', 'Error'));
	}

	close(): void {
		if (this.modalRef) { this.modalRef.hide(); }
	}
}
